<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisDataRegistryAPI from 'dashboard/api/laisDataRegistry';
import DataRegistryFieldGrid from './DataRegistryFieldGrid.vue';
import { buildFieldGroups } from '../utils/dataRegistryFields';
import { formatDateTime, riskTone } from '../utils/dataRegistryConsole';

const props = defineProps({
  show: { type: Boolean, default: false },
  kind: {
    type: String,
    default: 'record',
    validator: value => ['record', 'person'].includes(value),
  },
  itemId: { type: [String, Number], default: '' },
  fallbackItem: { type: Object, default: null },
});

const emit = defineEmits(['close', 'openRecord']);

const loading = ref(false);
const error = ref('');
const detail = ref(null);
const canViewUnminified = ref(false);
const activeSection = ref('fields');

const title = computed(() =>
  props.kind === 'person' ? 'Detalhe da pessoa' : 'Detalhe do registro'
);

const payload = computed(() => detail.value || props.fallbackItem?.raw || null);

const groups = computed(() => buildFieldGroups(payload.value, props.kind));

const linkedRecords = computed(() =>
  Array.isArray(payload.value?.records) ? payload.value.records : []
);

const headline = computed(() => {
  const source = payload.value || {};
  const personal =
    source.personal_data_unminified || source.personal_data_minified || {};
  return (
    personal.nome ||
    personal.name ||
    source.title ||
    source.name ||
    `#${props.itemId}`
  );
});

const fetchDetail = async () => {
  if (!props.show || !props.itemId) return;

  loading.value = true;
  error.value = '';
  try {
    const request =
      props.kind === 'person'
        ? LaisDataRegistryAPI.person(props.itemId)
        : LaisDataRegistryAPI.record(props.itemId);
    const { data } = await request;
    detail.value = data?.payload || null;
    canViewUnminified.value = Boolean(data?.can_view_unminified);
  } catch (requestError) {
    error.value =
      requestError?.response?.status === 404
        ? 'Item não encontrado no registry.'
        : 'Não foi possível carregar o detalhe.';
  } finally {
    loading.value = false;
  }
};

watch(
  () => [props.show, props.itemId, props.kind],
  () => {
    if (props.show) {
      detail.value = null;
      activeSection.value = 'fields';
      fetchDetail();
    }
  },
  { immediate: true }
);
</script>

<template>
  <LaisDrawer
    :show="show"
    :title="title"
    side="right"
    size="lg"
    @close="emit('close')"
  >
    <div
      class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
      data-test="data-registry-detail-drawer"
    >
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h2
            class="truncate font-redHatDisplay text-lg font-semibold text-lais-text"
          >
            {{ headline }}
          </h2>
          <p class="font-mono text-xs text-lais-text-muted">#{{ itemId }}</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <LaisStatusBadge
            :label="canViewUnminified ? 'dados completos' : 'redigido'"
            :tone="canViewUnminified ? 'info' : 'warning'"
            icon="i-lucide-shield"
          />
          <LaisStatusBadge
            v-if="payload?.risk_level"
            :label="payload.risk_level"
            :tone="riskTone(payload.risk_level)"
            dot
          />
        </div>
      </header>

      <LaisErrorState
        v-if="error"
        class="shadow-none"
        title="Falha ao carregar detalhe"
        :description="error"
        show-action
        :action-loading="loading"
        @action="fetchDetail"
      />

      <section
        v-else-if="loading"
        class="grid min-h-[200px] place-items-center text-sm text-lais-text-body"
        role="status"
        aria-busy="true"
      >
        <span>
          <span
            class="i-lucide-loader-circle mx-auto mb-3 block size-5 animate-spin"
            aria-hidden="true"
          />
          Carregando detalhe...
        </span>
      </section>

      <template v-else>
        <nav v-if="linkedRecords.length" class="flex gap-2" role="tablist">
          <button
            v-for="section in [
              { id: 'fields', label: 'Campos' },
              { id: 'records', label: `Registros (${linkedRecords.length})` },
            ]"
            :key="section.id"
            type="button"
            role="tab"
            :aria-selected="activeSection === section.id"
            class="rounded-lais border px-3 py-1 text-sm font-semibold"
            :class="
              activeSection === section.id
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border bg-white text-lais-text-body'
            "
            @click="activeSection = section.id"
          >
            {{ section.label }}
          </button>
        </nav>

        <DataRegistryFieldGrid
          v-if="activeSection === 'fields'"
          :groups="groups"
        />

        <ul v-else class="flex flex-col gap-2">
          <li
            v-for="linked in linkedRecords"
            :key="linked.id"
            class="rounded-lais-sm border border-lais-border-soft bg-white p-3"
          >
            <button
              type="button"
              class="flex w-full flex-col gap-1 text-left"
              @click="emit('openRecord', linked.id)"
            >
              <span class="font-semibold text-lais-text">
                {{ linked.title || `#${linked.id}` }}
              </span>
              <span class="text-xs text-lais-text-muted">
                {{ linked.source_name || linked.source_key || 'fonte —' }} ·
                {{ formatDateTime(linked.updated_at) }}
              </span>
            </button>
          </li>
        </ul>
      </template>
    </div>
  </LaisDrawer>
</template>
